import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import {
  ReportFooter,
  ReportHeader,
  ReportNote,
  ReportFlowPages,
  ReportSection,
} from '../../../components/ui/Report';
import { ageFromBirthDate } from '../config';
import { crewDisplayName } from '../formMappers';
import type { CrewFile } from '../types';
import type { CrewAiOverviewResponse } from '../../../services/mutations';
import { buildAtAGlanceMetrics, buildTenureMetrics, getCurrentAssignment } from './crewFilePrintMetrics';
import {
  defaultCrewFilePrintSections,
  type CrewFilePrintSectionSelection,
} from './crewFilePrintSections';

const CERT_CHUNK = 12;
const MEDICAL_CHUNK = 12;
const NOK_CHUNK = 10;
const ALLOTTEE_CHUNK = 8;
const FEEDBACK_CHUNK = 12;
const SERVICE_CHUNK = 10;
const DOC_CHUNK = 12;

function chunkArray<T>(items: T[], size: number): T[][] {
  if (items.length === 0) return [[]];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function fmtMoney(amount: number | string | null | undefined, currency?: string | null) {
  if (amount == null || amount === '') return '—';
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  const formatted = n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
}

function fmtYesNo(v?: boolean | null) {
  if (v == null) return '—';
  return v ? 'Yes' : 'No';
}

function EmptyRows({ colSpan }: { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className="crew-file-empty">
        None recorded
      </td>
    </tr>
  );
}

function MetricTiles({
  metrics,
  columns = 4,
}: {
  metrics: Array<{ label: string; value: string; hint?: string }>;
  columns?: 3 | 4 | 6 | 8;
}) {
  return (
    <div className={cn('crew-file-metrics', `crew-file-metrics-${columns}`)}>
      {metrics.map((m) => (
        <div key={m.label} className="crew-file-metric">
          <span className="crew-file-metric-label">{m.label}</span>
          <span className="crew-file-metric-value">{m.value}</span>
          {m.hint ? <span className="crew-file-metric-hint">{m.hint}</span> : null}
        </div>
      ))}
    </div>
  );
}

function StatusMark({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'info';
}) {
  return <span className={cn('crew-file-status', `crew-file-status-${tone}`)}>{children}</span>;
}

type ServiceHistoryRow = {
  key: string;
  vessel: React.ReactNode;
  rank: React.ReactNode;
  status: string;
  onBoard: boolean;
  signOn: string | null | undefined;
  signOff: string | null | undefined;
  coe: React.ReactNode;
  detail: React.ReactNode;
  reason: React.ReactNode;
};

function signOffSortKey(iso?: string | null): number {
  if (!iso) return Number.NEGATIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
}

function sectionTitle(base: string, total: number, chunkIndex: number, chunkCount: number) {
  if (chunkCount <= 1) return `${base} (${total})`;
  return `${base} (${total}) — ${chunkIndex + 1}/${chunkCount}`;
}

export type CrewFileReportProps = {
  item: CrewFile;
  companyLogoSrc?: string | null;
  companyName?: string | null;
  /** Which body sections to include (header always shown). */
  sections?: CrewFilePrintSectionSelection;
  /** Latest saved AI overview (optional). */
  aiOverview?: CrewAiOverviewResponse | null;
};

export const CrewFileReport: React.FC<CrewFileReportProps> = ({
  item,
  companyLogoSrc = null,
  companyName = null,
  sections = defaultCrewFilePrintSections(true),
  aiOverview = null,
}) => {
  const name = crewDisplayName(item);
  const age = ageFromBirthDate(item.birthDate);
  const certs = item.certs ?? [];
  const medicals = item.medicals ?? [];
  const nok = item.nextOfKin ?? [];
  const allottees = item.allottees ?? [];
  const appraisals = item.appraisals ?? [];
  const feedback = item.feedback ?? [];
  const prevServices = item.prevServices ?? [];
  const transactions = item.transactions ?? [];
  const documents = item.documents ?? [];

  const glanceMetrics = useMemo(() => buildAtAGlanceMetrics(item), [item]);
  const tenureMetrics = useMemo(() => buildTenureMetrics(item), [item]);
  const current = useMemo(() => getCurrentAssignment(item.transactions), [item.transactions]);
  const onBoard = current?.status === 'ONB';
  const overview = aiOverview?.overview;
  const readiness = overview?.readiness;
  const readinessTone =
    readiness === 'READY'
      ? 'ok'
      : readiness === 'CONDITIONAL'
        ? 'warn'
        : readiness === 'NOT_READY'
          ? 'danger'
          : 'neutral';

  const serviceHistory = useMemo(() => {
    const rows: ServiceHistoryRow[] = [
      ...transactions.map((t, i) => {
        const status = (t.status || '').toUpperCase();
        const onBoardRow = status === 'ONB';
        return {
          key: `tx-${t.id ?? i}`,
          vessel: dash(t.vessel?.vesselName || t.vesselName),
          rank: dash(t.rank?.name || t.rank?.abrv),
          status: status || '—',
          onBoard: onBoardRow,
          signOn: t.signOnDate || t.startDate,
          signOff: t.signOffDate || t.endDate,
          coe: fmtDate(t.coeDate),
          detail: fmtMoney(t.totalWage, t.wageCurrency),
          reason: dash(t.signOffReason?.name || t.notes),
        };
      }),
      ...prevServices.map((p, i) => ({
        key: `ps-${p.id ?? i}`,
        vessel: dash(p.vesselName),
        rank: dash(p.rank?.name || p.rank?.abrv),
        status: 'Prev',
        onBoard: false,
        signOn: p.signOnDate || p.startDate,
        signOff: p.signOffDate || p.endDate,
        coe: '—',
        detail:
          [
            p.vesselType?.name,
            p.engineType?.name || p.engType,
            p.vesselFlag?.name,
            p.managerName || p.manningAgency,
          ]
            .filter(Boolean)
            .join(' · ') || '—',
        reason: dash(p.signOffReason?.name || p.notes),
      })),
    ];

    rows.sort((a, b) => {
      if (a.onBoard !== b.onBoard) return a.onBoard ? -1 : 1;
      return signOffSortKey(b.signOff) - signOffSortKey(a.signOff);
    });
    return rows;
  }, [transactions, prevServices]);

  const firstHeader = useMemo(
    () => (
      <ReportHeader className="crew-file-header">
        <div className="crew-file-accent" aria-hidden />
        <div className="crew-file-masthead">
          <div className="crew-file-masthead-top">
            <div className="crew-file-masthead-brand">
              {companyLogoSrc ? (
                <img
                  src={companyLogoSrc}
                  alt={companyName || 'Management company logo'}
                  className="crew-file-logo"
                />
              ) : (
                <div className="crew-file-logo-fallback">
                  {(companyName || 'Atlas').slice(0, 1)}
                </div>
              )}
              <div className="crew-file-masthead-copy">
                <p className="crew-file-eyebrow">Crew file</p>
                {companyName ? <p className="crew-file-company">{companyName}</p> : null}
              </div>
            </div>
            <div className="crew-file-masthead-form">Personal record</div>
          </div>

          <div className="crew-file-identity" aria-label="Seafarer identity">
            <table className="crew-file-identity-table">
              <tbody>
                <tr>
                  <th scope="row">Name</th>
                  <td className="crew-file-identity-name">{name}</td>
                  <th scope="row">Status</th>
                  <td>
                    <div className="crew-file-identity-status">
                      <StatusMark tone={onBoard ? 'ok' : 'neutral'}>
                        {onBoard ? 'On board' : 'Off board'}
                      </StatusMark>
                      {item.availabilityStatus?.name &&
                      !/^(on|off)\s*board$/i.test(item.availabilityStatus.name.trim()) ? (
                        <StatusMark tone="info">{item.availabilityStatus.name}</StatusMark>
                      ) : null}
                      {item.notRecommended ? (
                        <StatusMark tone="danger">Not recommended</StatusMark>
                      ) : null}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th scope="row">Rank</th>
                  <td>{dash(item.rank?.name || item.rank?.abrv)}</td>
                  <th scope="row">Nationality</th>
                  <td>{dash(item.nationality?.name)}</td>
                </tr>
                <tr>
                  <th scope="row">Age</th>
                  <td>{age || '—'}</td>
                  <th scope="row">Ref</th>
                  <td>{item.crewRef?.trim() ? item.crewRef.trim() : `#${item.id}`}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="crew-file-id-strip">
          <div>
            <span className="crew-file-id-label">Manning agent</span>
            <span className="crew-file-id-value">{dash(item.manningAgent?.name)}</span>
          </div>
          <div>
            <span className="crew-file-id-label">Birth</span>
            <span className="crew-file-id-value">
              {fmtDate(item.birthDate)}
              {item.birthPlace ? ` · ${item.birthPlace}` : ''}
            </span>
          </div>
          <div>
            <span className="crew-file-id-label">Current vessel</span>
            <span className="crew-file-id-value">
              {dash(current?.vessel?.vesselName || current?.vesselName)}
            </span>
          </div>
          <div>
            <span className="crew-file-id-label">COE</span>
            <span className="crew-file-id-value">
              {fmtDate(current?.coeDate ?? current?.latestCoeDate ?? current?.extCoeDate)}
            </span>
          </div>
        </div>
      </ReportHeader>
    ),
    [
      age,
      companyLogoSrc,
      companyName,
      current?.coeDate,
      current?.extCoeDate,
      current?.latestCoeDate,
      current?.vessel?.vesselName,
      current?.vesselName,
      item.availabilityStatus?.name,
      item.birthDate,
      item.birthPlace,
      item.crewRef,
      item.id,
      item.manningAgent?.name,
      item.nationality?.name,
      item.notRecommended,
      item.rank?.abrv,
      item.rank?.name,
      name,
      onBoard,
    ],
  );

  const continueHeader = useMemo(
    () => (
      <div className="crew-file-continue-bar">
        <span className="crew-file-continue-title">Crew file</span>
        <span className="crew-file-continue-name">{name}</span>
        <span className="crew-file-continue-hint">continued</span>
      </div>
    ),
    [name],
  );

  const lastFooter = useMemo(
    () => (
      <ReportFooter>
        <ReportNote>
          Printed {new Date().toLocaleString('en-GB')}
          {companyName ? ` · ${companyName}` : ''} · Crew #{item.id}
          {item.crewRef?.trim() ? ` · Ref ${item.crewRef.trim()}` : ''}
        </ReportNote>
      </ReportFooter>
    ),
    [companyName, item.crewRef, item.id],
  );

  const { items, itemKeys } = useMemo(() => {
    const blocks: React.ReactNode[] = [];
    const keys: Array<string | number> = [];
    const push = (key: string, node: React.ReactNode) => {
      keys.push(key);
      blocks.push(node);
    };

    if (sections.aiOverview) {
      push(
        'ai',
        <ReportSection title="AI overview" className="crew-file-section">
          {overview ? (
            <div className="crew-file-ai">
              <div className="crew-file-ai-top">
                <div className="crew-file-ai-copy">
                  <p className="crew-file-ai-headline">{overview.headline}</p>
                  <p className="crew-file-ai-summary">{overview.summary}</p>
                </div>
                <StatusMark tone={readinessTone}>
                  {(readiness || 'UNKNOWN').replace(/_/g, ' ')}
                </StatusMark>
              </div>
              <div className="crew-file-ai-cols">
                {(
                  [
                    ['Strengths', overview.strengths],
                    ['Risks', overview.risks],
                    ['Recommendations', overview.recommendations],
                  ] as const
                ).map(([title, list]) => (
                  <div key={title} className="crew-file-ai-col">
                    <p className="crew-file-ai-col-title">{title}</p>
                    {list?.length ? (
                      <ul>
                        {list.map((text) => (
                          <li key={text}>{text}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="crew-file-muted">—</p>
                    )}
                  </div>
                ))}
              </div>
              <p className="crew-file-ai-meta">
                Generated{' '}
                {aiOverview?.generatedAt
                  ? new Date(aiOverview.generatedAt).toLocaleString('en-GB')
                  : '—'}
                {aiOverview?.model ? ` · ${aiOverview.model}` : ''}
              </p>
            </div>
          ) : (
            <p className="crew-file-muted">No saved AI overview for this crew file.</p>
          )}
        </ReportSection>,
      );
    }

    if (sections.atAGlance) {
      push(
        'glance',
        <ReportSection title="At a glance" className="crew-file-section">
          <MetricTiles metrics={glanceMetrics} columns={4} />
        </ReportSection>,
      );
    }

    if (sections.tenure) {
      push(
        'tenure',
        <ReportSection title="Tenure" className="crew-file-section">
          <MetricTiles metrics={tenureMetrics} columns={6} />
        </ReportSection>,
      );
    }

    if (sections.personal) {
      push(
        'personal',
        <ReportSection title="Personal" className="crew-file-section">
          <table className="mga-report-kv">
            <tbody>
              <tr>
                <th>Last name</th>
                <td>{dash(item.lastName)}</td>
                <th>First name</th>
                <td>{dash(item.firstName)}</td>
              </tr>
              <tr>
                <th>Middle name</th>
                <td>{dash(item.middleName)}</td>
                <th>Crew ref</th>
                <td>{dash(item.crewRef)}</td>
              </tr>
              <tr>
                <th>Father</th>
                <td>{dash(item.fatherName)}</td>
                <th>Mother</th>
                <td>{dash(item.motherName)}</td>
              </tr>
              <tr>
                <th>Sex</th>
                <td>{dash(item.sexType?.name)}</td>
                <th>Marital status</th>
                <td>{dash(item.maritalStatus?.name)}</td>
              </tr>
              <tr>
                <th>Religion</th>
                <td>{dash(item.religion?.name)}</td>
                <th>Children</th>
                <td>{item.noOfChildren != null ? String(item.noOfChildren) : '—'}</td>
              </tr>
              <tr>
                <th>Spouse</th>
                <td>{dash(item.wifeName)}</td>
                <th>Birth place</th>
                <td>{dash(item.birthPlace)}</td>
              </tr>
            </tbody>
          </table>
        </ReportSection>,
      );
    }

    if (sections.contact) {
      push(
        'contact',
        <ReportSection title="Contact & address" className="crew-file-section">
          <table className="mga-report-kv">
            <tbody>
              <tr>
                <th>Telephone 1</th>
                <td>{dash(item.telephone1)}</td>
                <th>Telephone 2</th>
                <td>{dash(item.telephone2)}</td>
              </tr>
              <tr>
                <th>Mobile 1</th>
                <td>{dash(item.mobile1)}</td>
                <th>Mobile 2</th>
                <td>{dash(item.mobile2)}</td>
              </tr>
              <tr>
                <th>Email 1</th>
                <td>{dash(item.email1)}</td>
                <th>Email 2</th>
                <td>{dash(item.email2)}</td>
              </tr>
              <tr>
                <th>Fax</th>
                <td>{dash(item.fax1)}</td>
                <th>Skype</th>
                <td>{dash(item.skypeAccount)}</td>
              </tr>
              <tr>
                <th>Home address</th>
                <td colSpan={3}>{dash(item.homeAddress)}</td>
              </tr>
              <tr>
                <th>Temp address</th>
                <td colSpan={3}>{dash(item.tempAddress)}</td>
              </tr>
              <tr>
                <th>Country</th>
                <td>{dash(item.country?.name)}</td>
                <th>Nearest port</th>
                <td>{dash(item.nearestPort?.name)}</td>
              </tr>
              <tr>
                <th>Nearest airport</th>
                <td>{dash(item.nearestAirport)}</td>
                <th>Bank account</th>
                <td>{dash(item.bankAccount)}</td>
              </tr>
            </tbody>
          </table>
        </ReportSection>,
      );
    }

    if (sections.availability) {
      push(
        'availability',
        <ReportSection title="Availability & recruitment" className="crew-file-section">
          <table className="mga-report-kv">
            <tbody>
              <tr>
                <th>Available from</th>
                <td>{fmtDate(item.availableFrom)}</td>
                <th>Available to</th>
                <td>{fmtDate(item.availableTo)}</td>
              </tr>
              <tr>
                <th>Last application</th>
                <td>{fmtDate(item.lastApplicationDate)}</td>
                <th>Last contacted</th>
                <td>{fmtDate(item.lastContactedDate)}</td>
              </tr>
              <tr>
                <th>Duties</th>
                <td colSpan={3}>{dash(item.duties)}</td>
              </tr>
              <tr>
                <th>Availability notes</th>
                <td colSpan={3}>{dash(item.availabilityNotes)}</td>
              </tr>
            </tbody>
          </table>
        </ReportSection>,
      );
    }

    if (sections.physical) {
      push(
        'physical',
        <ReportSection title="Physical / misc" className="crew-file-section">
          <table className="mga-report-kv">
            <tbody>
              <tr>
                <th>Height</th>
                <td>{dash(item.crewHeight)}</td>
                <th>Weight</th>
                <td>{dash(item.crewWeight)}</td>
              </tr>
              <tr>
                <th>Eye colour</th>
                <td>{dash(item.eyeColor)}</td>
                <th>Hair colour</th>
                <td>{dash(item.hairColor)}</td>
              </tr>
              <tr>
                <th>Shoes</th>
                <td>{dash(item.shoesSize)}</td>
                <th>Working clothes</th>
                <td>{dash(item.workingClothesSize)}</td>
              </tr>
            </tbody>
          </table>
        </ReportSection>,
      );
    }

    if (sections.certificates) {
      const chunks = chunkArray(certs, CERT_CHUNK);
      chunks.forEach((chunk, ci) => {
        push(
          `certs-${ci}`,
          <ReportSection
            title={sectionTitle('Documents', certs.length, ci, chunks.length)}
            className="crew-file-section"
          >
            <table className="mga-report-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Number</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th>Country</th>
                  <th>On vessel</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {chunk.length === 0 ? (
                  <EmptyRows colSpan={7} />
                ) : (
                  chunk.map((c, i) => (
                    <tr key={c.id ?? `cert-${ci}-${i}`}>
                      <td>{dash(c.certCatalog?.name || c.certCatalog?.abrv)}</td>
                      <td>{dash(c.certNumber)}</td>
                      <td>{fmtDate(c.issuedDate)}</td>
                      <td>{fmtDate(c.expiredDate)}</td>
                      <td>{dash(c.issueCountry?.name)}</td>
                      <td>{fmtYesNo(c.onVessel)}</td>
                      <td>{dash(c.notes)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ReportSection>,
        );
      });
    }

    if (sections.medicals) {
      const chunks = chunkArray(medicals, MEDICAL_CHUNK);
      chunks.forEach((chunk, ci) => {
        push(
          `meds-${ci}`,
          <ReportSection
            title={sectionTitle('Medicals', medicals.length, ci, chunks.length)}
            className="crew-file-section"
          >
            <table className="mga-report-table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Exam date</th>
                  <th>Next due</th>
                  <th>Result</th>
                  <th>Clinic</th>
                  <th>Cert no.</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {chunk.length === 0 ? (
                  <EmptyRows colSpan={7} />
                ) : (
                  chunk.map((m, i) => (
                    <tr key={m.id ?? `med-${ci}-${i}`}>
                      <td>{dash(m.kind)}</td>
                      <td>{fmtDate(m.examDate)}</td>
                      <td>{fmtDate(m.nextDueDate)}</td>
                      <td>{dash(m.result)}</td>
                      <td>{dash(m.clinicName)}</td>
                      <td>{dash(m.certificateNo)}</td>
                      <td>{dash(m.resultNotes || m.notes)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ReportSection>,
        );
      });
    }

    if (sections.nextOfKin) {
      const chunks = chunkArray(nok, NOK_CHUNK);
      chunks.forEach((chunk, ci) => {
        push(
          `nok-${ci}`,
          <ReportSection
            title={sectionTitle('Next of kin', nok.length, ci, chunks.length)}
            className="crew-file-section"
          >
            <table className="mga-report-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Relation</th>
                  <th>Phone</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {chunk.length === 0 ? (
                  <EmptyRows colSpan={7} />
                ) : (
                  chunk.map((n, i) => (
                    <tr key={n.id ?? `nok-${ci}-${i}`}>
                      <td>{dash(n.fullName)}</td>
                      <td>{dash(n.relation?.name)}</td>
                      <td>{dash(n.phone)}</td>
                      <td>{dash(n.mobile)}</td>
                      <td>{dash(n.email)}</td>
                      <td>{dash(n.homeAddress)}</td>
                      <td>{dash(n.notes)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ReportSection>,
        );
      });
    }

    if (sections.allottees) {
      const chunks = chunkArray(allottees, ALLOTTEE_CHUNK);
      chunks.forEach((chunk, ci) => {
        push(
          `allot-${ci}`,
          <ReportSection
            title={sectionTitle('Allottees', allottees.length, ci, chunks.length)}
            className="crew-file-section"
          >
            <table className="mga-report-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Relation</th>
                  <th>Amount</th>
                  <th>Frequency</th>
                  <th>Bank</th>
                  <th>Account</th>
                  <th>Period</th>
                  <th>Contact</th>
                </tr>
              </thead>
              <tbody>
                {chunk.length === 0 ? (
                  <EmptyRows colSpan={8} />
                ) : (
                  chunk.map((a, i) => (
                    <tr key={a.id ?? `allot-${ci}-${i}`}>
                      <td>{dash(a.fullName)}</td>
                      <td>{dash(a.relation?.name)}</td>
                      <td>{fmtMoney(a.amount, a.currency)}</td>
                      <td>{dash(a.frequency)}</td>
                      <td>{dash(a.bank)}</td>
                      <td>{dash(a.bankAccount)}</td>
                      <td>
                        {fmtDate(a.startDate)} → {fmtDate(a.endDate)}
                      </td>
                      <td>
                        {[a.phone, a.mobile, a.email].filter(Boolean).join(' · ') || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ReportSection>,
        );
      });
    }

    if (sections.appraisals) {
      if (appraisals.length === 0) {
        push(
          'appraisals-empty',
          <ReportSection title="Appraisals (0)" className="crew-file-section">
            <p className="ui-report-note">None recorded</p>
          </ReportSection>,
        );
      } else {
        appraisals.forEach((ap, i) => {
          push(
            `appraisal-${ap.id ?? i}`,
            <ReportSection
              title={
                appraisals.length === 1
                  ? 'Appraisals (1)'
                  : `Appraisal ${i + 1} of ${appraisals.length}`
              }
              className="crew-file-section"
            >
              <div className="crew-file-appraisal">
                <table className="mga-report-kv">
                  <tbody>
                    <tr>
                      <th>Date</th>
                      <td>{fmtDate(ap.assessmentDate)}</td>
                      <th>Type</th>
                      <td>{dash(ap.appraisalType || ap.kind?.name)}</td>
                    </tr>
                    <tr>
                      <th>Vessel</th>
                      <td>{dash(ap.vessel?.vesselName)}</td>
                      <th>Assessor</th>
                      <td>{dash(ap.assessorName)}</td>
                    </tr>
                    <tr>
                      <th>Employment</th>
                      <td>{dash(ap.employmentStatus?.name)}</td>
                      <th>Avg score</th>
                      <td>
                        {ap.questionnaireAvg != null ? String(ap.questionnaireAvg) : '—'}
                      </td>
                    </tr>
                    <tr>
                      <th>Promote</th>
                      <td>{fmtYesNo(ap.toBePromoted)}</td>
                      <th>Questionnaire</th>
                      <td>{dash(ap.questionnaire?.name)}</td>
                    </tr>
                    <tr>
                      <th>Notes</th>
                      <td colSpan={3}>{dash(ap.notes)}</td>
                    </tr>
                    {ap.badHabits?.trim() ? (
                      <tr>
                        <th>Bad habits</th>
                        <td colSpan={3}>{ap.badHabits}</td>
                      </tr>
                    ) : null}
                    {ap.dismissalReasonText?.trim() || ap.signOffReason?.name ? (
                      <tr>
                        <th>Dismissal / sign-off</th>
                        <td colSpan={3}>
                          {dash(ap.dismissalReasonText || ap.signOffReason?.name)}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
                {(ap.answers?.length ?? 0) > 0 ? (
                  <table className="mga-report-table" style={{ marginTop: 6 }}>
                    <thead>
                      <tr>
                        <th>Question</th>
                        <th>Answer</th>
                        <th>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ap.answers!.map((ans, j) => {
                        let answer: React.ReactNode = '—';
                        if (ans.answerTypeSnapshot === 'SCALE' && ans.scaleValue != null) {
                          answer = `${ans.scaleValue}${
                            ans.scaleMaxSnapshot != null ? ` / ${ans.scaleMaxSnapshot}` : ''
                          }`;
                        } else if (ans.answerTypeSnapshot === 'YES_NO_NA') {
                          answer = ans.choiceValue || '—';
                        } else if (ans.textValue) {
                          answer = ans.textValue;
                        }
                        return (
                          <tr key={ans.id ?? `ans-${i}-${j}`}>
                            <td>{dash(ans.questionLabelSnapshot)}</td>
                            <td>{answer}</td>
                            <td>{dash(ans.comment)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : null}
              </div>
            </ReportSection>,
          );
        });
      }
    }

    if (sections.feedback) {
      const chunks = chunkArray(feedback, FEEDBACK_CHUNK);
      chunks.forEach((chunk, ci) => {
        push(
          `feedback-${ci}`,
          <ReportSection
            title={sectionTitle('Feedback', feedback.length, ci, chunks.length)}
            className="crew-file-section"
          >
            <table className="mga-report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {chunk.length === 0 ? (
                  <EmptyRows colSpan={2} />
                ) : (
                  chunk.map((f, i) => (
                    <tr key={f.id ?? `fb-${ci}-${i}`}>
                      <td>{fmtDate(f.feedbackDate)}</td>
                      <td>{dash(f.notes)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ReportSection>,
        );
      });
    }

    if (sections.seaService) {
      const chunks = chunkArray(serviceHistory, SERVICE_CHUNK);
      chunks.forEach((chunk, ci) => {
        push(
          `service-${ci}`,
          <ReportSection
            title={sectionTitle('Sea service', serviceHistory.length, ci, chunks.length)}
            className="crew-file-section"
          >
            <table className="mga-report-table">
              <thead>
                <tr>
                  <th>Vessel</th>
                  <th>Rank</th>
                  <th>Status</th>
                  <th>Sign on</th>
                  <th>Sign off</th>
                  <th>COE</th>
                  <th>Wage / details</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {chunk.length === 0 ? (
                  <EmptyRows colSpan={8} />
                ) : (
                  chunk.map((row) => (
                    <tr key={row.key} className={row.onBoard ? 'crew-file-row-onb' : undefined}>
                      <td>{row.vessel}</td>
                      <td>{row.rank}</td>
                      <td>
                        <StatusMark
                          tone={
                            row.onBoard ? 'ok' : row.status === 'Prev' ? 'info' : 'neutral'
                          }
                        >
                          {row.status}
                        </StatusMark>
                      </td>
                      <td>{fmtDate(row.signOn)}</td>
                      <td>{row.onBoard ? 'On board' : fmtDate(row.signOff)}</td>
                      <td>{row.coe}</td>
                      <td>{row.detail}</td>
                      <td>{row.reason}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ReportSection>,
        );
      });
    }

    if (sections.documents) {
      const chunks = chunkArray(documents, DOC_CHUNK);
      chunks.forEach((chunk, ci) => {
        push(
          `docs-${ci}`,
          <ReportSection
            title={sectionTitle('Other files', documents.length, ci, chunks.length)}
            className="crew-file-section"
          >
            <table className="mga-report-table">
              <thead>
                <tr>
                  <th>Kind</th>
                  <th>Filename</th>
                  <th>Uploaded</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {chunk.length === 0 ? (
                  <EmptyRows colSpan={4} />
                ) : (
                  chunk.map((d) => (
                    <tr key={d.id}>
                      <td>{dash(d.kind)}</td>
                      <td>{dash(d.originalFilename)}</td>
                      <td>{fmtDate(d.createdAt)}</td>
                      <td>{dash(d.notes)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </ReportSection>,
        );
      });
    }

    if (sections.notes && item.crewNotes?.trim()) {
      push(
        'notes',
        <ReportSection title="Crew notes" className="crew-file-section">
          <p className="ui-report-note" style={{ whiteSpace: 'pre-wrap' }}>
            {item.crewNotes.trim()}
          </p>
        </ReportSection>,
      );
    }

    return { items: blocks, itemKeys: keys };
  }, [
    aiOverview?.generatedAt,
    aiOverview?.model,
    allottees,
    appraisals,
    certs,
    documents,
    feedback,
    glanceMetrics,
    item,
    medicals,
    nok,
    overview,
    readiness,
    readinessTone,
    sections,
    serviceHistory,
    tenureMetrics,
  ]);

  return (
    <ReportFlowPages
      className="crew-file-report"
      size="portrait"
      firstHeader={firstHeader}
      continueHeader={continueHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default CrewFileReport;
